import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Concierge, ConciergeCredentials } from '@azko/shared';

import { ApiRequestError, api } from '../lib/api.js';
import { formatPhone, formatReceived, maskPhone, phoneToE164 } from '../lib/format.js';
import { useSession } from '../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  LoadingRows,
  MaskedInput,
  PageHeader,
  StatusBadge,
  TextInput,
} from '../components/ui.js';

/**
 * Contas de portaria.
 *
 * Este era o buraco que impedia o produto de ser usado sem a gente por perto:
 * as permissoes `condominio:porteiros:*` existiam desde o RBAC e nenhuma rota
 * as usava, entao o sindico assinava, montava o predio, importava a lista de
 * moradores — e nao tinha como criar a conta de quem ia operar o balcao. So o
 * `db:seed` criava usuario, e ele nunca roda em producao.
 */

/**
 * A senha inicial, no unico momento em que ela existe em texto.
 *
 * Fica em destaque e nao some sozinha: o sindico precisa copiar ou anotar antes
 * de sair da tela, e o banco guarda so o hash. Perdeu, e redefinir — que e uma
 * acao a um clique daqui, de proposito.
 */
function Credentials({ data, onClose }: { data: ConciergeCredentials; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mb-4 rounded-2xl border border-accent/40 bg-accent/10 p-5">
      <p className="eyebrow text-accent">Senha inicial — anote agora</p>
      <p className="mt-2 text-sm text-muted">
        Ela nao pode ser vista de novo. Entregue ao porteiro junto com o e-mail de acesso.
      </p>

      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted">E-mail</dt>
          <dd className="mt-1 break-all font-mono text-sm text-fg">{data.email}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wider text-muted">Senha</dt>
          <dd className="mt-1 font-mono text-lg font-bold tracking-wide text-fg">
            {data.password}
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          variant="ghost"
          onClick={() => {
            // `clipboard` nao existe fora de HTTPS, e a portaria as vezes abre
            // o sistema por IP. Falhar aqui nao pode esconder a senha: ela
            // continua na tela para ser copiada a mao.
            void navigator.clipboard
              ?.writeText(`${data.email} / ${data.password}`)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? 'Copiado' : 'Copiar e-mail e senha'}
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Ja anotei
        </Button>
      </div>
    </div>
  );
}

function NewConciergeForm({ onCreated }: { onCreated: (data: ConciergeCredentials) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [shift, setShift] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<ConciergeCredentials>('/concierges', {
        name,
        email,
        ...(phone ? { phoneE164: phoneToE164(phone) } : {}),
        ...(shift ? { shift } : {}),
      }),
    onSuccess: (data) => {
      setName('');
      setEmail('');
      setPhone('');
      setShift('');
      setError(null);
      onCreated(data);
      void queryClient.invalidateQueries({ queryKey: ['concierges'] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError ? err.message : 'Nao foi possivel criar a conta.',
      ),
  });

  return (
    <Card className="mb-4">
      <CardHeader
        title="Novo porteiro"
        subtitle="A senha inicial aparece uma unica vez, aqui na tela."
      />

      <form
        className="grid gap-4 px-5 pb-5 sm:px-6 lg:grid-cols-5"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <Field label="Nome" className="lg:col-span-2">
          <TextInput
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Carlos Souza"
            maxLength={120}
            required
          />
        </Field>

        <Field label="E-mail de acesso" className="lg:col-span-2">
          <TextInput
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="carlos@portaria.com"
            maxLength={160}
            required
          />
        </Field>

        <Field label="Turno">
          <TextInput
            value={shift}
            onChange={(event) => setShift(event.target.value)}
            placeholder="Noturno"
            maxLength={32}
          />
        </Field>

        <Field label="Telefone" className="lg:col-span-2">
          <MaskedInput
            mask={maskPhone}
            value={phone}
            onValueChange={setPhone}
            placeholder="(31) 9 9999-1122"
          />
        </Field>

        <div className="flex items-end lg:col-span-3">
          <Button type="submit" loading={create.isPending} disabled={!name || !email}>
            Criar conta
          </Button>
        </div>

        {error && (
          <div className="lg:col-span-5">
            <Alert>{error}</Alert>
          </div>
        )}
      </form>
    </Card>
  );
}

/**
 * Corrige nome, telefone e turno do porteiro.
 *
 * `PUT /concierges/:id` existia no backend desde o comeco e NENHUMA tela o
 * chamava: dava para criar a conta, cortar o acesso e redefinir a senha, mas
 * nao para arrumar um nome digitado errado. Quem trocava de turno virava uma
 * conta nova, e o historico de encomendas ficava partido entre as duas.
 *
 * O e-mail nao esta aqui de proposito: ele e a identidade da conta e o backend
 * so o aceita na criacao — trocar o e-mail seria criar outra pessoa por baixo
 * do mesmo historico.
 */
function EditConciergeForm({
  concierge,
  onDone,
  onError,
}: {
  concierge: Concierge;
  onDone: () => void;
  onError: (error: unknown) => void;
}) {
  const [name, setName] = useState(concierge.name);
  const [phone, setPhone] = useState(maskPhone(concierge.phoneE164 ?? ''));
  const [shift, setShift] = useState(concierge.shift);

  const save = useMutation({
    mutationFn: () =>
      api.put<void>(`/concierges/${concierge.id}`, {
        name: name.trim(),
        phoneE164: phoneToE164(phone),
        shift: shift.trim(),
      }),
    onSuccess: onDone,
    onError,
  });

  return (
    <form
      className="mt-3 w-full rounded-2xl border border-accent/40 bg-accent/5 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Nome">
          <TextInput
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            autoFocus
          />
        </Field>

        <Field label="Telefone">
          <MaskedInput
            mask={maskPhone}
            value={phone}
            onValueChange={setPhone}
            placeholder="(31) 9 9999-1122"
          />
        </Field>

        <Field label="Turno">
          <TextInput
            value={shift}
            onChange={(event) => setShift(event.target.value)}
            placeholder="Noturno"
            maxLength={32}
          />
        </Field>
      </div>

      <p className="mt-3 text-xs text-muted">
        O e-mail de acesso nao muda: ele identifica a conta e o historico de encomendas dela.
      </p>

      <div className="mt-3">
        <Button type="submit" loading={save.isPending} disabled={name.trim().length < 2}>
          Salvar
        </Button>
      </div>
    </form>
  );
}

export function ConciergesPage() {
  const { allows } = useSession();
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = useState<ConciergeCredentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const canCreate = allows('condominio:porteiros:criar');
  const canEdit = allows('condominio:porteiros:editar');
  const canReset = allows('condominio:porteiros:resetar_senha');
  const canCut = allows('condominio:porteiros:cortar_acesso');
  const canRestore = allows('condominio:porteiros:liberar_acesso');

  const { data, isLoading } = useQuery({
    queryKey: ['concierges'],
    queryFn: () => api.get<{ items: Concierge[] }>('/concierges'),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => api.post<ConciergeCredentials>(`/concierges/${id}/senha`),
    onSuccess: (result) => {
      setError(null);
      setCredentials(result);
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError ? err.message : 'Nao foi possivel redefinir a senha.',
      ),
  });

  const setAccess = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.put<void>(`/concierges/${id}/acesso`, { active }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['concierges'] });
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError ? err.message : 'Nao foi possivel alterar o acesso.',
      ),
  });

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader title="Porteiros" subtitle="Cada conta guarda quem registrou cada encomenda." />

      {credentials && (
        <Credentials data={credentials} onClose={() => setCredentials(null)} />
      )}
      {error && (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {canCreate && <NewConciergeForm onCreated={setCredentials} />}

      <Card>
        <CardHeader title="Contas de portaria" />

        {isLoading ? (
          <LoadingRows />
        ) : items.length === 0 ? (
          <EmptyState
            title="Nenhum porteiro cadastrado"
            description={
              canCreate
                ? 'Crie a primeira conta acima — sem ela, so voce consegue registrar encomenda.'
                : 'Peca ao sindico para criar as contas de portaria.'
            }
          />
        ) : (
          <ul className="divide-y divide-line-soft">
            {items.map((concierge) => (
              <li
                key={concierge.id}
                className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-6"
              >
                <div className="min-w-0">
                  <p className="truncate font-bold">{concierge.name}</p>
                  <p className="truncate text-sm text-muted">{concierge.email}</p>
                  <p className="mt-1 text-sm text-muted">
                    {concierge.shift && <span className="text-fg">{concierge.shift} • </span>}
                    {concierge.phoneE164 ? formatPhone(concierge.phoneE164) : 'sem telefone'}
                    {' • '}
                    {/* "Nunca entrou" e o sinal de que a senha inicial ficou no
                        bolso do sindico e o porteiro segue registrando no papel. */}
                    {concierge.lastLoginAt
                      ? `ultimo acesso ${formatReceived(concierge.lastLoginAt)}`
                      : 'nunca entrou'}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge tone={concierge.status === 'active' ? 'accent' : 'warn'}>
                    {concierge.status === 'active' ? 'Ativo' : 'Sem acesso'}
                  </StatusBadge>

                  {canEdit && (
                    <Button
                      variant="quiet"
                      className="py-1.5"
                      onClick={() => {
                        setError(null);
                        setEditing(editing === concierge.id ? null : concierge.id);
                      }}
                    >
                      {editing === concierge.id ? 'Fechar' : 'Editar'}
                    </Button>
                  )}

                  {canReset && concierge.status === 'active' && (
                    <Button
                      variant="quiet"
                      className="py-1.5"
                      loading={resetPassword.isPending && resetPassword.variables === concierge.id}
                      onClick={() => resetPassword.mutate(concierge.id)}
                    >
                      Redefinir senha
                    </Button>
                  )}

                  {concierge.status === 'active'
                    ? canCut && (
                        <Button
                          variant="quiet"
                          className="py-1.5"
                          onClick={() =>
                            setAccess.mutate({ id: concierge.id, active: false })
                          }
                        >
                          Cortar acesso
                        </Button>
                      )
                    : canRestore && (
                        <Button
                          variant="quiet"
                          className="py-1.5"
                          onClick={() => setAccess.mutate({ id: concierge.id, active: true })}
                        >
                          Liberar acesso
                        </Button>
                      )}
                </div>

                {editing === concierge.id && (
                  <EditConciergeForm
                    concierge={concierge}
                    onDone={() => {
                      setEditing(null);
                      setError(null);
                      void queryClient.invalidateQueries({ queryKey: ['concierges'] });
                    }}
                    onError={(err) =>
                      setError(
                        err instanceof ApiRequestError
                          ? err.message
                          : 'Nao foi possivel salvar.',
                      )
                    }
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
